import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  ConnectionStatus,
  ChannelMapping,
  MessageListener,
  RawMessageListener,
} from '@/services/websocket/websocket.types.ts';

export type { ConnectionStatus } from '@/services/websocket/websocket.types.ts';

interface WebSocketStore {
  // State
  status: ConnectionStatus;
  subscriptions: string[];
  error: Error | null;
  reconnectAttempts: number;
  channelMappings: Map<string, ChannelMapping[]>;
  messageListeners: Map<string, Set<MessageListener>>;
  rawMessageListeners: Set<RawMessageListener>;

  // State setters
  setStatus: (status: ConnectionStatus) => void;
  setSubscriptions: (subscriptions: string[]) => void;
  setError: (error: Error | null) => void;
  incrementReconnectAttempts: () => void;
  resetReconnectAttempts: () => void;
  reset: () => void;

  // Listener/mapping registration (returns cleanup function)
  registerChannelMapping: (channel: string, mapping: ChannelMapping) => () => void;
  addMessageListener: (channel: string, listener: MessageListener) => () => void;
  addRawMessageListener: (listener: RawMessageListener) => () => void;
}

const initialState = {
  status: 'disconnected' as ConnectionStatus,
  subscriptions: [] as string[],
  error: null as Error | null,
  reconnectAttempts: 0,
  channelMappings: new Map<string, ChannelMapping[]>(),
  messageListeners: new Map<string, Set<MessageListener>>(),
  rawMessageListeners: new Set<RawMessageListener>(),
};

export const useWebSocketStore = create<WebSocketStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setStatus: (status) => set({ status }),

      setSubscriptions: (subscriptions) => set({ subscriptions }),

      setError: (error) => set({ error }),

      incrementReconnectAttempts: () =>
        set((state) => ({ reconnectAttempts: state.reconnectAttempts + 1 })),

      resetReconnectAttempts: () => set({ reconnectAttempts: 0 }),

      reset: () =>
        set({
          status: 'disconnected',
          subscriptions: [],
          error: null,
          reconnectAttempts: 0,
        }),

      registerChannelMapping: (channel, mapping) => {
        const { channelMappings } = get();
        if (!channelMappings.has(channel)) {
          channelMappings.set(channel, []);
        }
        channelMappings.get(channel)!.push(mapping);

        return () => {
          const mappings = channelMappings.get(channel);
          if (mappings) {
            const index = mappings.indexOf(mapping);
            if (index !== -1) {
              mappings.splice(index, 1);
            }
            if (mappings.length === 0) {
              channelMappings.delete(channel);
            }
          }
        };
      },

      addMessageListener: (channel, listener) => {
        const { messageListeners } = get();
        if (!messageListeners.has(channel)) {
          messageListeners.set(channel, new Set());
        }
        messageListeners.get(channel)!.add(listener);

        return () => {
          const listeners = messageListeners.get(channel);
          if (listeners) {
            listeners.delete(listener);
            if (listeners.size === 0) {
              messageListeners.delete(channel);
            }
          }
        };
      },

      addRawMessageListener: (listener) => {
        const { rawMessageListeners } = get();
        rawMessageListeners.add(listener);

        return () => {
          rawMessageListeners.delete(listener);
        };
      },
    }),
    { name: 'websocket-store' }
  )
);
